import { EFieldType, EViewType } from 'src/enums/ESmartCRM';
import { TCustomer, TSmartVariableDefinition } from '@/types/TSmartCRM';
import type { Rule } from 'antd/es/form';
import { notification } from 'antd';
import { getLeads } from '@/api/crm/smartCRM';
import { FilterView } from './crmDashboardUtils';

// ====== COMMON HELPERS ======

const getVariableValue = (customer: any, fieldId: string): any => {
	if (
		customer.variables &&
		typeof customer.variables === 'object' &&
		!Array.isArray(customer.variables)
	) {
		return customer.variables[fieldId];
	}
	if (customer.variables && Array.isArray(customer.variables)) {
		const match = customer.variables.find((v: any) => v.variableId === fieldId);
		return match?.value;
	}
	return undefined;
};

const processFieldValue = (value: any, fieldType: EFieldType, fieldName?: string): string => {
	if (value === null || value === undefined) {
		return fieldName || 'Unassigned';
	}

	if (fieldType === EFieldType.SELECT) {
		return String(value);
	}
	if (fieldType === EFieldType.MULTISELECT) {
		return Array.isArray(value) ? value[0] || 'Unassigned' : String(value);
	}
	if (fieldType === EFieldType.BOOLEAN) {
		if (value === true || value === 'true' || value === 'Yes' || value === 1 || value === '1') {
			return 'Yes';
		}
		if (value === false || value === 'false' || value === 'No' || value === 0 || value === '0') {
			return 'No';
		}
		return 'Unassigned';
	}
	return fieldName || 'Unassigned';
};

export const renderValue = (value: any, type: EFieldType): string => {
	if (value === null || value === undefined || value === '') {
		return '';
	}
	if (type === EFieldType.BOOLEAN) {
		return value ? 'Yes' : 'No';
	}
	if (type === EFieldType.MULTISELECT) {
		return Array.isArray(value) ? value.join(', ') : value;
	}
	if (type === EFieldType.DATE) {
		return new Date(value).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		});
	}
	return value.toString();
};

const collectMultiselectValues = (groupedCustomers: Record<string, any[]>): string[] => {
	const allValues = new Set<string>();
	Object.values(groupedCustomers).forEach((customers) => {
		customers.forEach((customer) => {
			const addArrayValues = (arr: any[]) => arr.forEach((item: string) => allValues.add(item));

			if (
				customer.variables &&
				typeof customer.variables === 'object' &&
				!Array.isArray(customer.variables)
			) {
				Object.values(customer.variables).forEach(
					(val: any) => Array.isArray(val) && addArrayValues(val),
				);
			} else if (Array.isArray(customer.variables)) {
				customer.variables.forEach((v: any) => Array.isArray(v.value) && addArrayValues(v.value));
			} else if (Array.isArray(customer.multipleItems)) {
				addArrayValues(customer.multipleItems);
			}
		});
	});
	return Array.from(allValues).sort();
};

const processVariables = (customer: TCustomer, fields: TSmartVariableDefinition[]) => {
	const tags = [];
	const addTag = (variableId: string, value: any, keyOverride?: string) => {
		const field = fields.find((f) => f.id === variableId);
		if (field && value !== null && value !== undefined && value !== '') {
			const displayValue = renderValue(value, field.fieldType);
			if (displayValue) {
				tags.push({ key: keyOverride || variableId, label: field.fieldName, value: displayValue });
			}
		}
	};

	if (typeof customer.variables === 'object' && !Array.isArray(customer.variables)) {
		Object.entries(customer.variables).forEach(([id, val]) => addTag(id, val));
	} else if (Array.isArray(customer.variables)) {
		customer.variables.forEach((v) => addTag(v.variableId, v.value, v.id));
	}
	return tags;
};

// ====== PUBLIC FUNCTIONS ======

export const generateInternalKey = (name: string): string => {
	return name
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9\s]/g, '')
		.replace(/\s+/g, '_');
};

export const getCustomerGroupValue = (
	customer: any,
	groupField: TSmartVariableDefinition,
): string => {
	const variableValue = getVariableValue(customer, groupField.id);

	if (variableValue !== null && variableValue !== undefined) {
		const processed = processFieldValue(variableValue, groupField.fieldType, groupField.fieldName);
		return processed;
	}

	if (customer.pickFromList && groupField.fieldType === EFieldType.SELECT) {
		return customer.pickFromList;
	}
	if (customer.multipleItems && groupField.fieldType === EFieldType.MULTISELECT) {
		const result = Array.isArray(customer.multipleItems)
			? customer.multipleItems[0] || 'Unassigned'
			: customer.multipleItems;
		return result;
	}

	if (groupField.fieldType === EFieldType.BOOLEAN) {
		const booleanValue = customer[groupField.internalKey];

		if (booleanValue === true || booleanValue === 'true' || booleanValue === 'Yes') {
			return 'Yes';
		}
		if (booleanValue === false || booleanValue === 'false' || booleanValue === 'No') {
			return 'No';
		}
	}
	return 'Unassigned';
};

export const generateGroupOptions = (
	fieldOptions: string[],
	fieldType: EFieldType,
	fieldName: string,
	groupedCustomers: Record<string, any[]>,
	hasUnassigned: boolean,
	isFilterView: boolean = false,
	customGroupNames?: string[],
): string[] => {
	const unassigned = hasUnassigned ? ['Unassigned'] : [];

	if (isFilterView && customGroupNames && customGroupNames.length > 0) {
		return [...customGroupNames];
	}

	if (fieldOptions.length > 0 && fieldType === EFieldType.SELECT) {
		return [...fieldOptions, ...unassigned];
	}
	if (fieldType === EFieldType.MULTISELECT) {
		const customerValues = collectMultiselectValues(groupedCustomers);
		const allOptions = fieldOptions.length > 0 ? fieldOptions : customerValues;
		return [...allOptions, ...unassigned];
	}
	if (fieldType === EFieldType.BOOLEAN) {
		return ['Yes', 'No', ...unassigned];
	}

	const dynamicGroups = Object.keys(groupedCustomers)
		.filter((key) => key !== 'Unassigned')
		.sort();
	return [...(dynamicGroups.length ? dynamicGroups : [fieldName || 'Default']), ...unassigned];
};

export const getGroupDisplayText = (
	customers: any[],
	groupBy: string | null,
	groupByOptions: Array<{ label: string; value: string }>,
): string => {
	if (!groupBy) {
		return `Showing ${customers.length} customers`;
	}

	const groupName = groupByOptions.find((opt) => opt.value === groupBy)?.label || 'field';
	const filtered = customers.filter(
		(c) => c[groupBy] !== null && c[groupBy] !== undefined && c[groupBy] !== '',
	);
	return `Showing ${filtered.length} customers grouped by ${groupName}`;
};

export const getFieldType = (fieldType: EFieldType): string => {
	const types = {
		[EFieldType.NUMBER]: 'number',
		[EFieldType.BOOLEAN]: 'switch',
		[EFieldType.DATE]: 'date',
	};
	return types[fieldType] || 'input';
};

export const getFieldRules = (variable: TSmartVariableDefinition): Rule[] => {
	const rules: Rule[] = [];
	if (variable.isRequired) {
		rules.push({ required: true, message: `${variable.fieldName} is required` });
	}
	if (variable.fieldType === EFieldType.NUMBER) {
		rules.push({ type: 'number', message: 'Please enter a valid number' });
	}
	return rules;
};

export const getFieldPlaceholder = (variable: TSmartVariableDefinition): string => {
	if (variable.fieldType === EFieldType.BOOLEAN) {
		return '';
	}
	const action = variable.fieldType === EFieldType.DATE ? 'Select' : 'Enter';
	return `${action} ${variable.fieldName.toLowerCase()}`;
};

export const createGroupByOptions = (fields: TSmartVariableDefinition[]) => {
	return fields.map((field) => ({ label: field.fieldName, value: field.internalKey }));
};

export const shouldApplyFilter = (view: EViewType, groupBy: string) =>
	view !== EViewType.TABLE && groupBy;

export const getDisplayCustomers = (
	activeFilterView: string | null,
	filterViews: FilterView[],
	groupBy: string,
	filteredCustomers: TCustomer[],
	customers: TCustomer[],
): TCustomer[] => {
	if (activeFilterView) {
		const activeView = filterViews.find((v) => v.id === activeFilterView);
		return activeView?.filteredCustomers || [];
	}
	return groupBy ? filteredCustomers : customers;
};

export const getDisplayLoading = (
	customersLoading: boolean,
	filterLoading: boolean,
	view: EViewType,
) => {
	return customersLoading || (filterLoading && view !== EViewType.TABLE);
};

export const fetchFilteredLeads = async (
	fieldId: string,
	agentId: string,
	view: EViewType,
	pagination?: { limit?: number; lastEvaluatedKey?: string | null },
	filterGroups?: any,
): Promise<{
	leads: TCustomer[];
	count: number;
	pagination?: any;
	groups?: Record<string, TCustomer[]>;
	groupMetadata?: Record<
		string,
		{
			name: string;
			id: string;
		}
	>;
} | null> => {
	try {
		const params: any = {
			agentId,
			includeVariables: 'true',
			sortBy: 'createdAt',
			sortOrder: 'desc',
			pagination: {
				limit: pagination?.limit || 10,
				lastEvaluatedKey: pagination?.lastEvaluatedKey || null,
			},
		};

		if (filterGroups) {
			params.filterGroups = JSON.stringify(filterGroups);
		} else {
			params.variableId = fieldId;
		}

		const response = await getLeads(params);

		if (response.success) {
			if (response.data?.groups) {
				const allLeads = Object.values(response.data.groups).flat() as TCustomer[];

				const result = {
					leads: allLeads,
					count: allLeads.length,
					pagination: response.data?.pagination,
					groups: response.data.groups as Record<string, TCustomer[]>,
					groupMetadata: response.data?.groupMetadata,
				};
				return result;
			} else {
				const result = {
					leads: response.data?.leads || [],
					count: response.data?.count || response.data?.leads?.length || 0,
					pagination: response.data?.pagination,
				};
				return result;
			}
		}
		return null;
	} catch (error: any) {
		showErrorNotification('Error fetching filtered leads', error?.message);
		return null;
	}
};

export const showSuccessNotification = (message: string, description?: string) => {
	notification.success({ message, description, placement: 'topRight' });
};

export const showErrorNotification = (message: string, description?: string) => {
	notification.error({
		message,
		description: description || 'An unexpected error occurred',
		placement: 'topRight',
	});
};

export const renderFieldValue = (value: any, type: EFieldType): string => renderValue(value, type);

export const getCustomerFieldTags = (customer: TCustomer, fields: TSmartVariableDefinition[]) => {
	return customer.variables ? processVariables(customer, fields) : [];
};
